import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { updateUserDetailsService } from '../../services/auth.service';
import { useAuth } from '../../context/auth';

const TutorialContext = createContext();

// sessionStorage marker set when the user closes the tutorial via the ✕ WITHOUT
// ticking "do not show again". Suppresses auto-start for the rest of the current
// login session only: it survives page refreshes but is wiped on tab close and
// cleared explicitly on logout, so the tutorial returns on the next login.
// A single tab holds one logged-in user, so a constant (non-user-keyed) key is
// enough — and avoids depending on authUser.id being present at click time.
export const TUTORIAL_SESSION_DISMISS_KEY = 'tutorial_session_dismissed';

export const useTutorial = () => {
    return useContext(TutorialContext);
};

export const TutorialProvider = ({ children, stepsConfig, engineConfig, authUser }) => {
    const [isActive, setIsActive] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [steps, setSteps] = useState([]);

    // Holds optional per-invocation callbacks (used by situational/custom tutorials).
    // We use a ref so changes don't cause re-renders.
    const sessionCallbacksRef = useRef(null);
    // Keep a ref so dismiss callbacks never close over stale authUser
    const authUserRef = useRef(authUser);
    useEffect(() => { authUserRef.current = authUser; }, [authUser]);

    const { setAuthUser } = useAuth();

    useEffect(() => {
        if (stepsConfig && stepsConfig.length > 0) {
            setSteps(stepsConfig);
        }
    }, [stepsConfig]);

    /**
     * startTutorial
     *   @param {Array|null}  customSteps  - Override step list for this invocation.
     *   @param {object|null} callbacks    - Optional { onFinish, onSkip } for custom tutorials.
     */
    const startTutorial = (customSteps = null, callbacks = null) => {
        if (customSteps && Array.isArray(customSteps) && customSteps.length > 0) {
            setSteps(customSteps);
        } else if (stepsConfig && stepsConfig.length > 0) {
            setSteps(stepsConfig);
        } else {
            return;
        }
        sessionCallbacksRef.current = callbacks || null;
        setCurrentStepIndex(0);
        setIsActive(true);
    };

    const stopTutorial = () => {
        setIsActive(false);
        sessionCallbacksRef.current = null;
        if (stepsConfig) setSteps(stepsConfig);
    };

    // Persists the tutorial enabled/disabled flag to the API and authUser context.
    // Called immediately when the "Don't show again" checkbox is toggled.
    const setInsiderTutorialFlag = (enabled) => {
        updateUserDetailsService({ show_insider_tutorial: enabled }).catch(() => {});
        const current = authUserRef.current;
        if (current) {
            setAuthUser({ ...current, show_insider_tutorial: enabled });
        }
    };

    // Convenience wrapper used by skip/finish when neverShowAgain is true
    const _disableTutorial = () => setInsiderTutorialFlag(false);

    // Session-scoped dismissal: hide the tutorial for the rest of this login
    // session without persisting the permanent opt-out flag. Used when the user
    // closes via the ✕ (no "do not show again"). Stored in localStorage with a
    // timestamp so the dismissal state is shared across all tabs/new windows.
    const _dismissForSession = () => {
        try {
            localStorage.setItem(TUTORIAL_SESSION_DISMISS_KEY, String(Date.now()));
        } catch (e) { /* storage unavailable — non-critical */ }
    };

    const skipTutorial = (neverShowAgain = true) => {
        setIsActive(false);

        const cb = sessionCallbacksRef.current;
        sessionCallbacksRef.current = null;

        if (stepsConfig) setSteps(stepsConfig);

        if (cb) {
            // Custom/situational invocation — fire the caller's own callback.
            // Do NOT touch the global tutorial_completed key.
            if (typeof cb.onSkip === 'function') cb.onSkip();
        } else if (neverShowAgain) {
            _disableTutorial();
        } else {
            // Closed via the ✕ without "do not show again": suppress for the rest
            // of this login session only (returns on the next login).
            _dismissForSession();
        }
    };

    // Called when the user clicks "Finish" on the last step
    const finishTutorial = () => {
        setIsActive(false);

        const cb = sessionCallbacksRef.current;
        sessionCallbacksRef.current = null;

        if (stepsConfig) setSteps(stepsConfig);

        if (cb) {
            if (typeof cb.onFinish === 'function') cb.onFinish();
        } else {
            _disableTutorial();
        }
    };

    const nextStep = () => {
        if (currentStepIndex < steps.length - 1) {
            setCurrentStepIndex(prev => prev + 1);
        } else {
            // Last step — treat as finish
            finishTutorial();
        }
    };

    const prevStep = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(prev => prev - 1);
        }
    };

    // Auto-start: use show_insider_tutorial from the auth object.
    // false  → never show (user opted out)
    // true / undefined → show (new users default to seeing it)
    useEffect(() => {
        if (!authUser || !stepsConfig || stepsConfig.length === 0) return;
        const canShow = authUser.is_email_verified && !authUser.required_password_reset;
        if (!canShow || isActive) return;

        if (authUser.show_insider_tutorial === false) return;

        // Dismissed via ✕ earlier in this login session — stay hidden until the
        // next login or until TTL (1 hour) expires (shared across tabs via localStorage).
        try {
            const dismissedAtStr = localStorage.getItem(TUTORIAL_SESSION_DISMISS_KEY);
            if (dismissedAtStr) {
                const dismissedAt = parseInt(dismissedAtStr, 10);
                const oneHour = 60 * 60 * 1000;
                if (!isNaN(dismissedAt) && (Date.now() - dismissedAt < oneHour)) {
                    return;
                }
            }
        } catch (e) { /* storage unavailable — fall through and show */ }

        startTutorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepsConfig, authUser]); // Intentionally omitting isActive/startTutorial to prevent loops

    const value = {
        isActive,
        currentStepIndex,
        currentStep: steps[currentStepIndex],
        totalSteps: steps.length,
        config: engineConfig,
        startTutorial,
        stopTutorial,
        skipTutorial,
        finishTutorial,
        nextStep,
        prevStep,
        setInsiderTutorialFlag,
    };

    return (
        <TutorialContext.Provider value={value}>
            {children}
        </TutorialContext.Provider>
    );
};
